/**
 * Fee estimation types for the console UI.
 * Mirrors the Rust FeeSummary / FeeBreakdown schema.
 */

export interface FeeLineItem {
  category: string;
  count: number;
  unit_cost_micro: string;
  total_cost_micro: string;
  description: string;
}

export interface FunctionFeeBreakdown {
  function_name: string;
  line_items: FeeLineItem[];
  total_fee_micro: string;
}

export interface FeeSummary {
  contract_name: string;
  max_fee_micro: string;
  max_fee_display: string;
  deploy_fee_micro: string;
  deploy_fee_display: string;
  storage_fee_micro: string;
  deploy_complexity: 'Simple' | 'Medium' | 'Complex' | null;
  functions: FunctionFeeSummaryItem[];
}

export interface FunctionFeeCategoryDetail {
  category: string;
  label: string;
  count: number;
  unit_cost: number;
  total_cost: number;
  total_display: string;
}

export interface FunctionFeeSummaryItem {
  function_name: string;
  fee_micro: string;
  fee_display: string;
  category_count: number;
  categories: FunctionFeeCategoryDetail[];
}

export interface FeeEstimateResponse {
  breakdown: {
    contract_name: string;
    functions: FunctionFeeBreakdown[];
    function_fees: Record<string, string>;
    max_fee_micro: string;
    deploy_fee_micro: string;
    storage_fee_micro: string;
    deploy_complexity: 'Simple' | 'Medium' | 'Complex' | null;
    prefab_percentage_fee_micro: string;
  };
  summary: FeeSummary;
  explanation: string[];
}

/** Format a USD micros string to a human-readable USD display.
 * Internal unit: 1 USD = 1,000,000 micros. */
export function formatUsd(micro: string): string {
  const value = BigInt(micro);
  const USD_BASE = BigInt(1_000_000);
  const whole = value / USD_BASE;
  const frac = value % USD_BASE;
  if (frac === BigInt(0)) {
    return `$${whole}.0`;
  }
  const fracStr = frac.toString().padStart(6, '0').replace(/0+$/, '');
  return `$${whole}.${fracStr}`;
}
